// ================================================================
// src/pages/admin/AdminTeacherShelf.jsx
// ================================================================
// صفحه‌ی مدیر برای مدیریت اساتید برگزیده‌ی هر پایه/رشته
// داده در: /api/db/teacherShelfConfigs/{grade}-{field}
// ================================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';

import {
  BooksEngineProvider, useBooksEngine, useEngineReady,
} from '../../hooks/useBooksEngine.js';

import dataEngine from '../../core/books/data.js';
import { engine } from '../../core/books/storage.js';
import { getToken } from '../../utils/secureStorage';

import {
  GRADE_LIST, FIELD_LIST, getSubjectsFor, needsField as curriculumNeedsField,
} from '../../core/education/curriculum.js';
import { SUBJECT_LABELS } from '../../core/books/data.js';

import './admin-books.css';
import './AdminTeacherShelf.css';

// ================================================================
// ENTRY
// ================================================================
export default function AdminTeacherShelfPage() {
  const { user } = useAuth();
  if (!user || user.role !== 'admin') {
    return <Navigate to="/AdminHome" replace />;
  }
  return (
    <BooksEngineProvider user={user}>
      <AdminTeacherShelfInner />
    </BooksEngineProvider>
  );
}

// ================================================================
// MAIN INNER
// ================================================================
function AdminTeacherShelfInner() {
  const navigate = useNavigate();
  const { user, toast } = useBooksEngine();
  const { ready } = useEngineReady();

  // ── State ──
  const [activeGrade, setActiveGrade] = useState('1');
  const [activeField, setActiveField] = useState(null);
  const [activeSubject, setActiveSubject] = useState('');
  const [allConfigs, setAllConfigs] = useState({});
  const [draftItems, setDraftItems] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimerRef = useRef(null);

  const [isDarkMode] = useState(() => {
    try { return engine.getRaw('navbar2-drawer-dark') === '1'; } catch { return false; }
  });

  // ── Effective field ──
  const effectiveField = useMemo(() => {
    if (!curriculumNeedsField(activeGrade)) return null;
    if (activeField) return activeField;
    return FIELD_LIST[0]?.id || null;
  }, [activeGrade, activeField]);

  const configKey = useMemo(
    () => `${activeGrade}-${effectiveField || 'none'}`,
    [activeGrade, effectiveField]
  );

  // ── Toast helper ──
  const showToast = useCallback((msg, tone = 'success') => {
    setToastMsg({ msg, tone });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMsg(null), 3000);
  }, []);

  // ── Load all configs ──
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    (async () => {
      try {
        const token = getToken();
        const res = await fetch('/api/db/teacherShelfConfigs', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          if (!cancelled) setAllConfigs({});
          return;
        }
        const list = await res.json();
        const map = {};
        if (Array.isArray(list)) {
          for (const cfg of list) {
            if (cfg?.id) map[cfg.id] = cfg;
          }
        }
        if (!cancelled) setAllConfigs(map);
      } catch {
        if (!cancelled) setAllConfigs({});
      }
    })();
    return () => { cancelled = true; };
  }, [ready]);

  // ── Load draft for current key ──
  useEffect(() => {
    setLoadingConfig(true);
    const cfg = allConfigs[configKey];
    const items = Array.isArray(cfg?.items) ? cfg.items : [];
    setDraftItems(items.map((it, idx) => ({ ...it, order: idx })));
    setIsDirty(false);
    setLoadingConfig(false);
    setEditingItem(null);
    setMenuItem(null);
  }, [configKey, allConfigs]);

  // ── Teachers for selected subject ──
  const teacherOptions = useMemo(() => {
    if (!ready) return [];
    if (!curriculumNeedsField(activeGrade) && !activeGrade) return [];

    try {
      if (activeSubject) {
        const list = dataEngine.query.getTeachersForSubject(
          activeGrade, activeSubject, effectiveField || null
        ) || [];
        return list.map((t) => ({
          ...t,
          _subject: activeSubject,
          _subjectTitle: SUBJECT_LABELS[activeSubject] || activeSubject,
        }));
      }
      const subjects = getSubjectsFor(activeGrade, effectiveField || null) || [];
      const all = [];
      for (const s of subjects) {
        const list = dataEngine.query.getTeachersForSubject(
          activeGrade, s.id, effectiveField || null
        ) || [];
        for (const t of list) {
          all.push({
            ...t,
            _subject: s.id,
            _subjectTitle: s.title || SUBJECT_LABELS[s.id] || s.id,
          });
        }
      }
      return all;
    } catch {
      return [];
    }
  }, [ready, activeGrade, effectiveField, activeSubject]);

  const filteredTeachers = useMemo(() => {
    let list = teacherOptions;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((t) =>
        String(t.name || '').toLowerCase().includes(q) ||
        String(t._subjectTitle || '').toLowerCase().includes(q)
      );
    }
    if (filterMode === 'selected') {
      list = list.filter((t) =>
        draftItems.some((it) => it.teacherId === t.id && it.subject === t._subject)
      );
    }
    return list;
  }, [teacherOptions, searchQuery, filterMode, draftItems]);

  // 🆕 Resolve blob avatar URLs (برای استادانی که فقط avatarBlobId دارند)
  const [resolvedAvatars, setResolvedAvatars] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ids = new Set();
      for (const t of teacherOptions) ids.add(t.id);
      for (const it of draftItems) ids.add(it.teacherId);

      const newEntries = {};
      for (const id of ids) {
        if (id in resolvedAvatars) continue;
        const teacher = dataEngine.teacher.get(id);
        if (!teacher) { newEntries[id] = null; continue; }
        if (teacher.avatarBlobId) {
          try {
            const url = await engine.blobs.getUrl(teacher.avatarBlobId);
            newEntries[id] = url || null;
          } catch { newEntries[id] = null; }
        } else {
          newEntries[id] = null;
        }
      }

      if (!cancelled && Object.keys(newEntries).length > 0) {
        setResolvedAvatars(prev => ({ ...prev, ...newEntries }));
      }
    })();
    return () => { cancelled = true; };
    
  }, [teacherOptions, draftItems]);

  
  const toggleTeacher = useCallback((teacher) => {
    setDraftItems((prev) => {
      const idx = prev.findIndex(
        (it) => it.teacherId === teacher.id && it.subject === teacher._subject
      );
      if (idx >= 0) {
        return prev.filter((_, i) => i !== idx).map((it, i) => ({ ...it, order: i }));
      }
      return [
        ...prev,
        {
          id: `itm_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          teacherId: teacher.id,
          projectId: teacher.projectId,
          subject: teacher._subject,
          field: effectiveField || null,
          displayName: null,
          displaySubject: null,
          isSpecial: null,
          order: prev.length,
        },
      ];
    });
    setIsDirty(true);
  }, [effectiveField]);

  // ── Reorder ──
  const moveItem = useCallback((itemId, dir) => {
    setDraftItems((prev) => {
      const idx = prev.findIndex((it) => it.id === itemId);
      if (idx < 0) return prev;
      const target = dir === 'left' ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((it, i) => ({ ...it, order: i }));
    });
    setIsDirty(true);
  }, []);

  // ── Drag reorder (HTML5) ──
  const dragFromRef = useRef(null);
  const onDragStart = (e, itemId) => {
    dragFromRef.current = itemId;
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (e, toItemId) => {
    e.preventDefault();
    const fromId = dragFromRef.current;
    dragFromRef.current = null;
    if (!fromId || fromId === toItemId) return;
    setDraftItems((prev) => {
      const fromIdx = prev.findIndex((it) => it.id === fromId);
      const toIdx = prev.findIndex((it) => it.id === toItemId);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next.map((it, i) => ({ ...it, order: i }));
    });
    setIsDirty(true);
  };

  // ── Delete ──
  const removeItem = useCallback((itemId) => {
    setDraftItems((prev) =>
      prev.filter((it) => it.id !== itemId).map((it, i) => ({ ...it, order: i }))
    );
    setIsDirty(true);
    setMenuItem(null);
  }, []);

  // ── Edit item ──
  const saveEditedItem = useCallback((itemId, patch) => {
    setDraftItems((prev) =>
      prev.map((it) => (it.id === itemId ? { ...it, ...patch } : it))
    );
    setIsDirty(true);
    setEditingItem(null);
  }, []);

  // ── Publish ──
  const publish = useCallback(async () => {
    setSaving(true);
    try {
      const token = getToken();
      const body = {
        id: configKey,
        grade: activeGrade,
        field: effectiveField || null,
        items: draftItems.map((it, i) => ({ ...it, order: i })),
        updatedAt: Date.now(),
        updatedBy: user?.id || null,
      };
      const res = await fetch(`/api/db/teacherShelfConfigs/${configKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        showToast('خطا در انتشار — لطفاً دوباره تلاش کنید', 'error');
        return;
      }
      setAllConfigs((prev) => ({ ...prev, [configKey]: body }));
      setIsDirty(false);
      showToast('منتشر شد ✓', 'success');
      toast?.('اساتید برگزیده برای این پایه منتشر شد', 'success');
    } catch {
      showToast('خطای شبکه — لطفاً دوباره تلاش کنید', 'error');
    } finally {
      setSaving(false);
    }
  }, [configKey, activeGrade, effectiveField, draftItems, user?.id, toast, showToast]);

  // ── Reset draft ──
  const resetDraft = useCallback(() => {
    const cfg = allConfigs[configKey];
    const items = Array.isArray(cfg?.items) ? cfg.items : [];
    setDraftItems(items.map((it, idx) => ({ ...it, order: idx })));
    setIsDirty(false);
    showToast('بازگردانی شد', 'info');
  }, [allConfigs, configKey, showToast]);

  // ── Grade change ──
  const handleGradeChange = (g) => {
    if (isDirty && !window.confirm('تغییرات ذخیره‌نشده از بین می‌رود. ادامه؟')) return;
    setActiveGrade(g);
    setActiveField(null);
    setActiveSubject('');
    setSearchQuery('');
  };

  const handleFieldChange = (f) => {
    if (isDirty && !window.confirm('تغییرات ذخیره‌نشده از بین می‌رود. ادامه؟')) return;
    setActiveField(f);
    setActiveSubject('');
    setSearchQuery('');
  };

  // ── Before unload ──
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── Loading state ──
  if (!ready) {
    return (
      <div className={`ab-page ${isDarkMode ? 'dark-mode' : ''}`}>
        <main className="ab-main" style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 32 }}>⏳</div>
          <div style={{ marginTop: 10 }}>در حال بارگذاری...</div>
        </main>
      </div>
    );
  }

  const subjectsList = getSubjectsFor(activeGrade, effectiveField || null) || [];
  const configBadges = Object.keys(allConfigs).reduce((acc, k) => {
    const count = Array.isArray(allConfigs[k]?.items) ? allConfigs[k].items.length : 0;
    acc[k] = count;
    return acc;
  }, {});

  return (
    <div className={`ab-page ats-page ${isDarkMode ? 'dark-mode' : ''}`} dir="rtl">
      {/* ── Header ── */}
      <header className="ats-header">
        <div className="ats-header-inner">
          <button
            type="button"
            className="ab-icon-btn"
            onClick={() => navigate('/admin/books')}
            title="بازگشت"
          >
            ←
          </button>
          <div className="ats-title-group">
            <h1 className="ats-title">⭐ مدیریت اساتید برگزیده</h1>
            <div className="ats-subtitle">
              برای هر پایه/رشته، اساتید و ترتیبشان را انتخاب کنید
            </div>
          </div>
          <div className="ats-header-actions">
            <span className={`ats-dirty ${isDirty ? 'is-dirty' : ''}`}>
              {isDirty ? '● تغییرات ذخیره نشده' : '✓ ذخیره شده'}
            </span>
          </div>
        </div>
      </header>

      <main className="ats-main">
        {/* ── Grade tabs ── */}
        <section className="ats-section">
          <label className="ats-section-label">پایه تحصیلی</label>
          <div className="ats-grade-tabs">
            {GRADE_LIST.map((g) => {
              const isActive = activeGrade === g.id;
              const keyBase = curriculumNeedsField(g.id)
                ? Object.keys(configBadges).filter((k) => k.startsWith(`${g.id}-`)).some((k) => configBadges[k] > 0)
                : (configBadges[`${g.id}-none`] || 0) > 0;
              return (
                <button
                  key={g.id}
                  type="button"
                  className={`ats-grade-tab ${isActive ? 'is-active' : ''}`}
                  onClick={() => handleGradeChange(g.id)}
                >
                  <span>{g.title}</span>
                  {keyBase && <span className="ats-grade-dot" />}
                </button>
              );
            })}
          </div>
        </section>

        {/* ── Field tabs (10-12) ── */}
        {curriculumNeedsField(activeGrade) && (
          <section className="ats-section">
            <label className="ats-section-label">رشته تحصیلی</label>
            <div className="ats-field-tabs">
              {FIELD_LIST.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`ats-field-tab ${effectiveField === f.id ? 'is-active' : ''}`}
                  onClick={() => handleFieldChange(f.id)}
                >
                  {f.title}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ── Subject dropdown ── */}
        <section className="ats-section">
          <label className="ats-section-label">کتاب درسی</label>
          <select
            className="ats-subject-select"
            value={activeSubject}
            onChange={(e) => setActiveSubject(e.target.value)}
          >
            <option value="">همه دروس</option>
            {subjectsList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title || SUBJECT_LABELS[s.id] || s.id}
              </option>
            ))}
          </select>
        </section>

        {/* ── Teacher selection grid ── */}
        <section className="ats-section">
          <div className="ats-section-header-row">
            <label className="ats-section-label">
              انتخاب اساتید ({filteredTeachers.length})
            </label>
            <div className="ats-filter-chips">
              <button
                type="button"
                className={`ats-filter-chip ${filterMode === 'all' ? 'is-active' : ''}`}
                onClick={() => setFilterMode('all')}
              >
                همه
              </button>
              <button
                type="button"
                className={`ats-filter-chip ${filterMode === 'selected' ? 'is-active' : ''}`}
                onClick={() => setFilterMode('selected')}
              >
                انتخاب‌شده‌ها ({draftItems.length})
              </button>
            </div>
          </div>

          <input
            className="ats-search-input"
            type="text"
            placeholder="🔍 جستجوی استاد یا درس..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {filteredTeachers.length === 0 ? (
            <div className="ats-empty">
              {teacherOptions.length === 0
                ? 'هنوز هیچ استاد منتشرشده‌ای برای این پایه/کتاب وجود ندارد.'
                : 'نتیجه‌ای یافت نشد.'}
            </div>
          ) : (
            <div className="ats-teacher-grid">
              {filteredTeachers.map((t) => {
                const isSelected = draftItems.some(
                  (it) => it.teacherId === t.id && it.subject === t._subject
                );
                return (
                  <button
                    key={`${t.id}-${t._subject}`}
                    type="button"
                    className={`ats-teacher-card ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => toggleTeacher(t)}
                  >
                    <div className="ats-teacher-avatar-wrap">
                      {(t.image || resolvedAvatars[t.id]) ? (
                        <img
                          src={t.image || resolvedAvatars[t.id]}
                          alt={t.name}
                          className="ats-teacher-avatar"
                          loading="lazy"
                        />
                      ) : (
                        <div className="ats-teacher-avatar-fallback">
                          {(t.name || 'م').charAt(0)}
                        </div>
                      )}
                      {isSelected && <span className="ats-teacher-check">✓</span>}
                    </div>
                    <div className="ats-teacher-info">
                      <div className="ats-teacher-name">{t.name}</div>
                      <div className="ats-teacher-subject">{t._subjectTitle}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Preview section ── */}
        <section className="ats-section">
          <label className="ats-section-label">
            پیش‌نمایش زنده ({draftItems.length} کارت)
          </label>

          {draftItems.length === 0 ? (
            <div className="ats-empty ats-empty-preview">
              هنوز استادی انتخاب نکردید. این پایه در صفحه‌ی دانش‌آموز، کپسول اساتید را نمایش نخواهد داد.
            </div>
          ) : (
            <div className="ats-preview">
              <div className="ats-preview-head">
                <span>✨ اساتید برگزیده</span>
              </div>
              <div className="ats-preview-viewport">
                {draftItems.map((item, idx) => {
                  const teacher = dataEngine.teacher.get(item.teacherId);
                  const project = dataEngine.project.get(item.projectId);
                  const isMissing = !teacher || !project;
                  const displayName = item.displayName || teacher?.fullName || '—';
                  const displaySubject = item.displaySubject || SUBJECT_LABELS[item.subject] || item.subject;
                  const image = teacher?.legacyAvatar || resolvedAvatars[item.teacherId] || null;
                  const isSpecial = item.isSpecial !== null && item.isSpecial !== undefined
                    ? !!item.isSpecial
                    : !!(teacher?.legacyIsSpecial || project?.stats?.isSpecial);

                  return (
                    <div
                      key={item.id}
                      className={`ats-preview-card ${isMissing ? 'is-missing' : ''}`}
                      draggable
                      onDragStart={(e) => onDragStart(e, item.id)}
                      onDragOver={onDragOver}
                      onDrop={(e) => onDrop(e, item.id)}
                    >
                      <span className="ats-drag-handle" title="جابجایی">⠿</span>
                      <button
                        type="button"
                        className="ats-item-menu-btn"
                        onClick={() => setMenuItem(menuItem === item.id ? null : item.id)}
                      >
                        ⋮
                      </button>

                      <div className="ats-preview-ring">
                        <div className="ats-preview-inner">
                          {image ? (
                            <img src={image} alt={displayName} className="ats-preview-avatar" />
                          ) : (
                            <div className="ats-preview-fallback">{displayName.charAt(0)}</div>
                          )}
                        </div>
                        {isSpecial && <span className="ats-preview-crown">👑</span>}
                      </div>
                      <div className="ats-preview-name">{displayName}</div>
                      <div className="ats-preview-subject">{displaySubject}</div>

                      {menuItem === item.id && (
                        <div className="ats-item-menu">
                          <button type="button" onClick={() => { setEditingItem(item.id); setMenuItem(null); }}>
                            ✏️ ویرایش
                          </button>
                          <button type="button" onClick={() => { moveItem(item.id, 'left'); setMenuItem(null); }}>
                            ← جابجایی چپ
                          </button>
                          <button type="button" onClick={() => { moveItem(item.id, 'right'); setMenuItem(null); }}>
                            → جابجایی راست
                          </button>
                          <button
                            type="button"
                            className="danger"
                            onClick={() => removeItem(item.id)}
                          >
                            🗑️ حذف
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </main>

      {/* ── Bottom action bar ── */}
      <div className="ats-action-bar">
        <div className="ats-action-bar-inner">
          <span className={`ats-dirty ${isDirty ? 'is-dirty' : ''}`}>
            {isDirty ? '● تغییرات ذخیره نشده' : '✓ همه ذخیره شده'}
          </span>
          <div className="ats-action-bar-actions">
            <button
              type="button"
              className="ab-btn ab-btn-ghost"
              onClick={resetDraft}
              disabled={!isDirty || saving}
            >
              بازگردانی
            </button>
            <button
              type="button"
              className="ab-btn ab-btn-primary"
              onClick={publish}
              disabled={saving}
            >
              {saving ? '⏳ در حال انتشار...' : '🚀 انتشار'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Edit item modal ── */}
      {editingItem && (() => {
        const item = draftItems.find((it) => it.id === editingItem);
        if (!item) return null;
        const teacher = dataEngine.teacher.get(item.teacherId);
        return (
          <EditItemModal
            item={item}
            teacher={teacher}
            onClose={() => setEditingItem(null)}
            onSave={(patch) => saveEditedItem(item.id, patch)}
          />
        );
      })()}

      {/* ── Toast ── */}
      {toastMsg && (
        <div className={`ats-toast ats-toast-${toastMsg.tone}`}>
          {toastMsg.msg}
        </div>
      )}
    </div>
  );
}

// ================================================================
// EDIT ITEM MODAL
// ================================================================
function EditItemModal({ item, teacher, onClose, onSave }) {
  const [name, setName] = useState(item.displayName || '');
  const [subject, setSubject] = useState(item.displaySubject || '');
  const [specialMode, setSpecialMode] = useState(
    item.isSpecial === null || item.isSpecial === undefined
      ? 'default'
      : item.isSpecial
        ? 'special'
        : 'normal'
  );

  const submit = () => {
    onSave({
      displayName: name.trim() ? name.trim() : null,
      displaySubject: subject.trim() ? subject.trim() : null,
      isSpecial: specialMode === 'default' ? null : specialMode === 'special',
    });
  };

  return (
    <div className="ats-modal-backdrop" onClick={onClose}>
      <div className="ats-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ats-modal-header">
          <h3>✏️ ویرایش کارت</h3>
          <button type="button" className="ats-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ats-modal-body">
          <div className="ats-modal-field">
            <label>نام نمایشی</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={teacher?.fullName || 'مثلاً: مریم حسینی (ویژه)'}
              maxLength={100}
            />
            <small>خالی = استفاده از نام اصلی استاد</small>
          </div>
          <div className="ats-modal-field">
            <label>متن درس</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={SUBJECT_LABELS[item.subject] || item.subject}
              maxLength={100}
            />
            <small>خالی = استفاده از عنوان اصلی درس</small>
          </div>
          <div className="ats-modal-field">
            <label>وضعیت ویژه</label>
            <div className="ats-special-toggle">
              <button
                type="button"
                className={specialMode === 'default' ? 'is-active' : ''}
                onClick={() => setSpecialMode('default')}
              >
                پیش‌فرض
              </button>
              <button
                type="button"
                className={specialMode === 'special' ? 'is-active' : ''}
                onClick={() => setSpecialMode('special')}
              >
                ⭐ ویژه
              </button>
              <button
                type="button"
                className={specialMode === 'normal' ? 'is-active' : ''}
                onClick={() => setSpecialMode('normal')}
              >
                عادی
              </button>
            </div>
          </div>
        </div>
        <div className="ats-modal-footer">
          <button type="button" className="ab-btn ab-btn-ghost" onClick={onClose}>
            انصراف
          </button>
          <button type="button" className="ab-btn ab-btn-primary" onClick={submit}>
            ذخیره
          </button>
        </div>
      </div>
    </div>
  );
}